import React, { useEffect, useState } from 'react'
import { ToastNodeItem, useToastManager } from './useToastManager'
import { scheduleOnRN } from 'react-native-worklets'
import Animated, {
  Easing,
  LinearTransition,
  clamp,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withDecay,
  withSpring,
  withTiming,
} from 'react-native-reanimated'
import { Gesture, GestureDetector } from 'react-native-gesture-handler'

const MaxOffset = 200
const CloseOffset = 100

export const ToastContainer = ({
  node,
  duration = 5000,
  id,
  cratedAt,
}: ToastNodeItem) => {
  const removeToast = useToastManager((s) => s.removeToast)

  const translationX = useSharedValue(0)
  const prevTranslationX = useSharedValue(0)

  const [isInteracting, setIsInteracting] = useState(false)

  const animatedStyles = useAnimatedStyle(() => ({
    transform: [{ translateX: translationX.value }],
    opacity: interpolate(
      Math.abs(translationX.value),
      [0, CloseOffset, MaxOffset],
      [1, 1, 0]
    ),
  }))

  const pan = Gesture.Pan()
    .minDistance(1)
    .runOnJS(true)
    .onStart(() => {
      prevTranslationX.value = translationX.value
    })
    .onUpdate((event) => {
      translationX.value = clamp(
        prevTranslationX.value + event.translationX,
        -MaxOffset,
        MaxOffset
      )
      setIsInteracting(true)
    })
    .onFinalize((event) => {
      const diff = Math.abs(translationX.value - prevTranslationX.value)
      setIsInteracting(false)
      if (diff < CloseOffset) {
        return
      }
      translationX.set(
        withDecay({
          velocity: event.velocityY,
          clamp: [-MaxOffset, MaxOffset],
        })
      )
    })
    .onEnd(() => {
      const diff = Math.abs(translationX.value - prevTranslationX.value)

      if (diff < CloseOffset) {
        translationX.set(withSpring(0))
      } else {
        removeToast(id)
      }
    })

  useEffect(() => {
    if (isInteracting) {
      return
    }
    const timer = setInterval(() => {
      if (Date.now() - cratedAt < duration) return
      translationX.set(
        withTiming(
          MaxOffset,
          {
            duration: 200,
            easing: Easing.inOut(Easing.ease),
          },
          () => scheduleOnRN(removeToast, id)
        )
      )
    }, 100)

    return () => clearInterval(timer)
  }, [isInteracting, cratedAt, duration, id])

  return (
    <GestureDetector gesture={pan}>
      <Animated.View layout={LinearTransition} style={animatedStyles}>
        {node}
      </Animated.View>
    </GestureDetector>
  )
}
